/**
 * Quiz Size Classification System
 *
 * Provides size categorization for datasets and quizzes based on question count.
 * Helps users quickly understand time commitment and scope.
 */

export type QuizSize = 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL' | 'XXL+';

/**
 * Calculate quiz size category based on question count
 *
 * Scale:
 * - XS: 1-10 questions (~2-5 min)
 * - S: 11-25 questions (~5-10 min)
 * - M: 26-50 questions (~10-20 min)
 * - L: 51-100 questions (~20-40 min)
 * - XL: 101-200 questions (~40-80 min)
 * - XXL: 201-400 questions (~80-160 min)
 * - XXL+: 400+ questions (2+ hours)
 */
export function calculateQuizSize(questionCount: number): QuizSize {
  if (questionCount <= 10) return 'XS';
  if (questionCount <= 25) return 'S';
  if (questionCount <= 50) return 'M';
  if (questionCount <= 100) return 'L';
  if (questionCount <= 200) return 'XL';
  if (questionCount <= 400) return 'XXL';
  return 'XXL+';
}

/**
 * Get color classes for size badge
 *
 * Color psychology:
 * - Green (XS): Easy, quick, approachable
 * - Blue (S): Calm, manageable
 * - Yellow (M): Moderate attention needed
 * - Orange (L): Significant commitment
 * - Red (XL): Warning - long quiz ahead
 * - Purple (XXL): Special challenge
 * - Pink (XXL+): Epic challenge
 */
export function getSizeBadgeColor(size: QuizSize): string {
  switch (size) {
    case 'XS':
      return 'text-green-600 border-green-300 dark:text-green-400 dark:border-green-700';
    case 'S':
      return 'text-blue-600 border-blue-300 dark:text-blue-400 dark:border-blue-700';
    case 'M':
      return 'text-yellow-600 border-yellow-300 dark:text-yellow-400 dark:border-yellow-700';
    case 'L':
      return 'text-orange-600 border-orange-300 dark:text-orange-400 dark:border-orange-700';
    case 'XL':
      return 'text-red-600 border-red-300 dark:text-red-400 dark:border-red-700';
    case 'XXL':
      return 'text-purple-600 border-purple-300 dark:text-purple-400 dark:border-purple-700';
    case 'XXL+':
      return 'text-pink-600 border-pink-300 dark:text-pink-400 dark:border-pink-700';
    default:
      return 'text-gray-600 border-gray-300 dark:text-gray-400 dark:border-gray-700';
  }
}

/**
 * Get human-readable label for size
 */
export function getSizeLabel(size: QuizSize): string {
  const labels: Record<QuizSize, string> = {
    'XS': 'Extra Small Quiz',
    'S': 'Small Quiz',
    'M': 'Medium Quiz',
    'L': 'Large Quiz',
    'XL': 'Extra Large Quiz',
    'XXL': 'Huge Quiz',
    'XXL+': 'Epic Quiz',
  };
  return labels[size];
}

/**
 * Get question count range for a size category
 */
export function getSizeRange(size: QuizSize): string {
  const ranges: Record<QuizSize, string> = {
    'XS': '1-10',
    'S': '11-25',
    'M': '26-50',
    'L': '51-100',
    'XL': '101-200',
    'XXL': '201-400',
    'XXL+': '400+',
  };
  return ranges[size];
}

/**
 * Calculate estimated completion time
 *
 * Assumes ~20 seconds per question on average:
 * - Beginners: ~30 seconds per question
 * - Intermediate: ~20 seconds per question
 * - Experts: ~10-15 seconds per question
 *
 * Estimates are conservative (rounded up) to avoid user frustration.
 */
export function getEstimatedTime(questionCount: number): string {
  const seconds = questionCount * 20; // 20 seconds per question
  const minutes = Math.ceil(seconds / 60);

  if (minutes < 60) {
    return `~${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `~${hours}h`;
  }

  return `~${hours}h ${remainingMinutes}m`;
}

/**
 * Get all available size options for filtering
 */
export function getAllSizes(): Array<{ value: QuizSize; label: string; range: string }> {
  const sizes: QuizSize[] = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXL+'];
  return sizes.map((size) => ({
    value: size,
    label: size,
    range: getSizeRange(size),
  }));
}
